import { CstNodeLocation, IToken } from 'chevrotain';
import {
  ArgumentNode,
  DirectiveNode,
  ListTypeNode,
  Location,
  NameNode,
  NamedTypeNode,
  ObjectFieldNode,
  OperationTypeNode,
  Source,
  StringValueNode,
  Token,
  TokenKind,
  TypeNode,
  ValueNode,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';

import { AllCstTypes } from './generatedOperationTypes';

// TODO consider removing `children` and
// just inlining? Could also replace all [Token] with single Range?
function tokenToRange(
  token: Pick<IToken, 'startLine' | 'startColumn' | 'endLine' | 'endColumn'>,
): monacoEditor.Range {
  return monacoEditor.Range.lift({
    startLineNumber: token.startLine ?? 1,
    startColumn: token.startColumn ?? 1,
    endLineNumber: token.endLine ?? token.startLine ?? 1,
    endColumn: (token.endColumn ?? 1) + 1,
  });
}

function pointToToken(
  point: monacoEditor.Position,
  model: monacoEditor.editor.ITextModel,
): Token {
  return new Token(
    TokenKind.STRING,
    model.getOffsetAt(point),
    model.getOffsetAt(point),
    point.lineNumber,
    point.column,
    null,
    '',
  );
}

export function locationToAstLocation(
  location: CstNodeLocation,
  model: monacoEditor.editor.ITextModel,
): Location {
  const range = tokenToRange(location);
  return new Location(
    pointToToken(range.getStartPosition(), model),
    pointToToken(range.getEndPosition(), model),
    new Source(model.getValue()),
  );
}
export function locationToValue(
  location: CstNodeLocation,
  model: monacoEditor.editor.ITextModel,
): string {
  return model.getValueInRange(tokenToRange(location));
}

export function cstSharedConversions(model: monacoEditor.editor.ITextModel) {
  // TODO does this make sense, should this have a location at the end of the keyword?
  const nameDefault = (): NameNode => ({
    kind: 'Name',
    value: '',
  });

  // TODO does this make sense, should this have a location at the end of the keyword?
  const namedTypeDefault = (): NamedTypeNode => ({
    kind: 'NamedType',
    name: nameDefault(),
  });

  const operationTypeDefault = 'query' as const;

  // TODO does this make sense, should this have a location at the end of the keyword?
  const valueDefault = (): ValueNode => ({ kind: 'NullValue' });

  // TODO does this make sense, should this have a location at the end of the keyword?
  const typeDefault = (): TypeNode => ({
    kind: 'NamedType',
    name: { kind: 'Name', value: 'Unknown' },
  });

  const nameToAst = (
    node:
      | AllCstTypes['Name']
      | AllCstTypes['NameButNotOn']
      | AllCstTypes['NameButNotTrueOrFalseOrNull']
      | undefined,
  ): NameNode | undefined => {
    return (
      node && {
        kind: 'Name',
        value: locationToValue(node.location, model),
        loc: locationToAstLocation(node.location, model),
      }
    );
  };

  const typeToAst = (
    node: AllCstTypes['Type'] | undefined,
  ): TypeNode | undefined => {
    if (!node) {
      return undefined;
    }
    const namedType = node.children.NamedType?.[0];
    const listType = node.children.ListType?.[0];
    const innerType =
      (namedType && namedTypeToAst(namedType)) ||
      (listType && listTypeToAst(listType));
    if (node?.children.Exclamation) {
      return (
        innerType && {
          kind: 'NonNullType',
          type: innerType,
        }
      );
    }
    return innerType;
  };

  const listTypeToAst = (node: AllCstTypes['ListType']): ListTypeNode => {
    return {
      kind: 'ListType',
      type: typeToAst(node.children.Type?.[0]) ?? typeDefault(),
      loc: locationToAstLocation(node.location, model),
    };
  };

  const namedTypeToAst = (node: AllCstTypes['NamedType']): NamedTypeNode => {
    return {
      kind: 'NamedType',
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      loc: locationToAstLocation(node.location, model),
    };
  };

  const defaultValueToAst = (
    node: AllCstTypes['DefaultValue'] | undefined,
  ): ValueNode | undefined => {
    return node && valueToAst(node.children.Value?.[0]);
  };

  const directiveToAst = (node: AllCstTypes['Directive']): DirectiveNode => {
    return {
      kind: 'Directive',
      loc: locationToAstLocation(node.location, model),
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      arguments:
        node.children.Arguments?.[0].children.Argument?.map(argumentToAst),
    };
  };

  const argumentToAst = (node: AllCstTypes['Argument']): ArgumentNode => {
    const value = node.children.Value?.[0];
    return {
      kind: 'Argument',
      loc: locationToAstLocation(node.location, model),
      name: nameToAst(node.children.Name?.[0]) ?? nameDefault(),
      value: valueToAst(value) ?? valueDefault(),
    };
  };

  const valueToAst = (
    node: AllCstTypes['Value'] | undefined,
  ): ValueNode | undefined => {
    if (!node) {
      return undefined;
    }
    const loc = locationToAstLocation(node.location, model);
    const value = locationToValue(node.location, model);
    if (node.children.IntValue) {
      return { kind: 'IntValue', loc, value };
    }
    if (node.children.FloatValue) {
      return { kind: 'FloatValue', loc, value };
    }
    if (node.children.StringValue) {
      return stringToAst(node.children.StringValue[0]);
    }
    if (node.children.BooleanValue) {
      return { kind: 'BooleanValue', loc, value: value === 'true' };
    }
    if (node.children.NullValue) {
      return { kind: 'NullValue', loc };
    }
    if (node.children.EnumValue) {
      return { kind: 'EnumValue', loc, value };
    }
    if (node.children.ListValue) {
      const values =
        node.children.ListValue[0].children.Value?.flatMap(
          (valueNode) => valueToAst(valueNode) ?? [],
        ) ?? [];
      return { kind: 'ListValue', loc, values };
    }
    if (node.children.ObjectValue) {
      return {
        kind: 'ObjectValue',
        loc,
        fields:
          node.children.ObjectValue[0].children.ObjectField?.flatMap(
            (field): ObjectFieldNode => {
              const name = nameToAst(field.children.Name?.[0]);
              const valueNode = valueToAst(field.children.Value?.[0]);
              return {
                kind: 'ObjectField',
                name: name ?? nameDefault(),
                value: valueNode ?? valueDefault(),
              };
            },
          ) ?? [],
      };
    }
    return undefined;
  };

  const stringToAst = (
    node: CstNodeLocation | undefined,
  ): StringValueNode | undefined => {
    const value = node && locationToValue(node, model);
    if (!node || !value) {
      return undefined;
    }
    const quoteSize = value.startsWith('"""') ? 3 : 1;
    const valueWithoutQuotes = value.slice(quoteSize, -quoteSize);

    return {
      kind: 'StringValue',
      value: valueWithoutQuotes,
      loc: locationToAstLocation(node, model),
      block: value.startsWith('"""'),
    };
  };

  const operationTypeToAst = (
    node: AllCstTypes['OperationType'] | undefined,
  ): OperationTypeNode | undefined => {
    const children = node?.children ?? {};
    return children.Query
      ? 'query'
      : children.Mutation
        ? 'mutation'
        : children.Subscription
          ? 'subscription'
          : undefined;
  };

  return {
    nameDefault,
    namedTypeDefault,
    operationTypeDefault,
    valueDefault,
    typeDefault,
    nameToAst,
    typeToAst,
    namedTypeToAst,
    valueToAst,
    defaultValueToAst,
    directiveToAst,
    argumentToAst,
    stringToAst,
    operationTypeToAst,
  };
}
